import React, { useState, useRef } from "react";
import { api } from "../lib/api";
import { Download, Upload, Loader2, FileSpreadsheet, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const ImportBlock = ({ kind, title, subtitle, columns, testPrefix }) => {
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/import/template/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `vizepanel_ornek_${kind}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Şablon indirilemedi"); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/import/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      if (r.data.created > 0) toast.success(`${r.data.created} kayıt eklendi`);
      if (r.data.errors?.length > 0) toast.error(`${r.data.errors.length} satırda hata`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yüklenemedi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="grid-card p-8" data-testid={`import-${testPrefix}-block`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-overline flex items-center gap-2"><FileSpreadsheet size={12} /> {subtitle}</div>
          <h2 className="mt-2 font-display font-black text-2xl tracking-tight">{title}</h2>
        </div>
        <button onClick={downloadTemplate} className="h-10 px-4 text-sm border border-zinc-200 rounded-md hover:bg-zinc-100 flex items-center gap-2 flex-shrink-0" data-testid={`${testPrefix}-download-template`}>
          <Download size={13} /> Şablon İndir
        </button>
      </div>

      <p className="mt-3 text-sm text-zinc-500 max-w-2xl">Şablonu indirin, verilerinizi ekleyin ve buradan yükleyin. Şablondaki <b>Açıklama</b> sekmesinde her alan için kural yazılıdır.</p>

      <div className="mt-6 border-2 border-dashed border-zinc-300 rounded-md p-6 bg-zinc-50">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="font-medium">Excel dosyasını buraya sürükleyin ya da seçin</div>
            <div className="text-xs text-zinc-500 mt-1">.xlsx / .xls · İlk sekme veya "Veri" sekmesi okunur</div>
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onUpload} className="hidden" data-testid={`${testPrefix}-file-input`} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-11 px-5 bg-zinc-950 text-white rounded-md hover:bg-zinc-800 flex items-center gap-2 disabled:opacity-50" data-testid={`${testPrefix}-upload-btn`}>
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "İşleniyor…" : "Excel Yükle"}
          </button>
        </div>
      </div>

      <div className="mt-6">
        <div className="label-overline mb-2">Beklenen Kolonlar</div>
        <div className="flex flex-wrap gap-2">
          {columns.map((c) => (
            <span key={c} className="text-xs px-2 py-1 border border-zinc-200 rounded bg-white">{c}</span>
          ))}
        </div>
      </div>

      {result && (
        <div className="mt-6 border border-zinc-200 rounded-md p-4 bg-white" data-testid={`${testPrefix}-result`}>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-[#00913c]">
              <CheckCircle2 size={16} />
              <span className="font-display font-bold text-2xl">{result.created}</span>
              <span className="text-sm text-zinc-500">eklendi</span>
            </div>
            {result.errors?.length > 0 && (
              <div className="flex items-center gap-2 text-[#b71c1c]">
                <XCircle size={16} />
                <span className="font-display font-bold text-2xl">{result.errors.length}</span>
                <span className="text-sm text-zinc-500">hata</span>
              </div>
            )}
            <div className="text-xs text-zinc-500">/ toplam {result.total_rows} satır</div>
          </div>
          {result.errors?.length > 0 && (
            <div className="max-h-64 overflow-y-auto overflow-x-auto mt-4">
              <table className="w-full text-xs min-w-[420px]">
                <thead className="bg-zinc-50">
                  <tr className="text-left">
                    <th className="px-3 py-2 label-overline">Satır</th>
                    <th className="px-3 py-2 label-overline">Hata</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.slice(0, 100).map((e) => (
                    <tr key={`${e.row}-${e.error}`} className="border-t border-zinc-100">
                      <td className="px-3 py-2 font-medium">{e.row}</td>
                      <td className="px-3 py-2 text-[#b71c1c]">{e.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function ImportPage() {
  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="import-page">
      <div className="label-overline">Toplu Veri Aktarımı</div>
      <h1 className="mt-2 font-display font-black text-3xl md:text-4xl tracking-tight">Excel'den İçe Aktar</h1>
      <p className="mt-3 text-sm text-zinc-500 max-w-2xl">Eski müşteri ve ödeme kayıtlarınızı Excel şablonuyla kolayca yükleyin. Önce şablonu indirin, verilerinizi ekleyin, sonra yükleyin — hatalı satırlar özetlenir.</p>

      <div className="mt-8 space-y-6">
        <ImportBlock
          kind="cases"
          testPrefix="cases"
          title="Müşteri & Vize Kayıtları"
          subtitle="Adım 1"
          columns={["Ad Soyad *", "Pasaport No", "Pasaport Başlangıç", "Pasaport Bitiş", "Veren Makam", "TC Kimlik No", "Doğum Tarihi", "Ülke *", "Tedarikçi", "Aracı Kurum", "Seyahat Tarihi", "Durum", "Tedarikçi Maliyeti (EUR)", "Müşteri Tutar (EUR)", "Müşteri Tutar (TL)", "Para Birimi", "Notlar"]}
        />
        <ImportBlock
          kind="payments"
          testPrefix="payments"
          title="Müşteri Ödemeleri"
          subtitle="Adım 2"
          columns={["Müşteri (Ad Soyad veya Pasaport No) *", "Tarih", "Tutar EUR", "Tutar TL", "Para Birimi", "Banka", "Not"]}
        />
      </div>
    </div>
  );
}
